import React, { useState, useEffect } from "react";

import Header from "./Header";
import SortableTable from "../shared/mui/tables/frontend/SortableTable";
import TableRow from "./TableRow";
import ServiceModel from "./Modals/ServiceModel";
import ImpactLevel from "./Modals/ImpactLevel";
import Status from "./Modals/Status";
import ScrollToTopButton from "../shared/ScrollToTopButton";

import { getProducts } from "../../api/fedRamp";
import { getLoginStatus } from "../../api/user";
import { alphabeticalSort, numericalSortUndefined } from "../../util/sort";
import { impactLevelSortBuilder, providerSortBuilder } from "../../util/fedRamp";
import { prepareTextForSearch } from "../../util/format";
import { GENERIC_ERROR_STRING } from "../../constants";

import "../../styles/fedRamp.less";

export default function FedRamp() {
  const [fedRampProducts, setFedRampProducts] = useState(/** @type FedRampProduct[] */[]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [searchFilter, setSearchFilter] = useState("Product");
  const [searchText, setSearchText] = useState("");
  const [modal, setModal] = useState(null);
  const [hoverModal, setHoverModal] = useState(null);
  const [hoverModalDisplay, setHoverModalDisplay] = useState("Absolute");
  const [visibleColumn, setVisibleColumn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "NASA SEWP FedRAMP Compliant Products";
  }, []);

  useEffect(() => {
    function onScroll(e) {
      // outerHeight instead of innerHeight to work when zoomed in/out
      if ((window.outerHeight / 2) > window.scrollY) {
        setHoverModalDisplay("Absolute");
      } else {
        setHoverModalDisplay("Fixed");
      }
    }

    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    getProducts().then(({ data: fedRampProductsData }) => {
      setFedRampProducts(fedRampProductsData);
      setLoading(false);
    }).catch(error => {
      setError(error);
    });
  }, []);

  useEffect(() => {
    getLoginStatus().then(({ data: loggedInStatus }) => {
      setIsLoggedIn(loggedInStatus);
    }).catch(error => {
      setError(error);
    });
  }, []);

  function handleStatusMouseEnter(event) {
    setHoverModal(<Status hoverModalDisplay={hoverModalDisplay}/>);
    setVisibleColumn(4);
  }

  function handleStatusMouseLeave(event) {
    setHoverModal(null);
    setVisibleColumn(null);
  }

  const headerOptions = [
    {
      name: "Product",
      asc: (a, b) => alphabeticalSort(a, b, "product", "asc"),
      desc: (a, b) => alphabeticalSort(a, b, "product", "desc"),
    }, {
      name: "Provider", asc: providerSortBuilder("asc"), desc: providerSortBuilder("desc"),
    }, {
      name: "Service Model",
      asc: (a, b) => alphabeticalSort(a, b, "serviceModel", "asc"),
      desc: (a, b) => alphabeticalSort(a, b, "serviceModel", "desc"),
      onMouseEnter: (event) => {
        setHoverModal(<ServiceModel hoverModalDisplay={hoverModalDisplay}/>);
        setVisibleColumn(2);
      },
      onMouseLeave: (event) => {
        setHoverModal(null);
        setVisibleColumn(null);
      },
    }, {
      name: "Impact Level",
      asc: impactLevelSortBuilder("asc"),
      desc: impactLevelSortBuilder("desc"),
      onMouseEnter: (event) => {
        setHoverModal(<ImpactLevel hoverModalDisplay={hoverModalDisplay}/>);
        setVisibleColumn(3);
      },
      onMouseLeave: (event) => {
        setHoverModal(null);
        setVisibleColumn(null);
      },
    }, {
      name: "Status",
      asc: (a, b) => alphabeticalSort(a, b, "authorizationStatus", "asc"),
      desc: (a, b) => alphabeticalSort(a, b, "authorizationStatus", "desc"),
      onMouseEnter: handleStatusMouseEnter,
      onMouseLeave: handleStatusMouseLeave,
    }, {
      name: "Agency Authorization",
    }];

  const loggedInHeaderOption = {
    name: "Contract Holders",
    asc: (a, b) => numericalSortUndefined(a, b, "contractHoldersCount", "asc"),
    desc: (a, b) => numericalSortUndefined(a, b, "contractHoldersCount", "desc"),
  };

  function getFilteredProducts() {
    if (searchText !== "") {
      const reg = new RegExp(prepareTextForSearch(searchText));
      return fedRampProducts.filter((fedRampProduct) => {
        if (searchFilter === "Product") {
          return reg.test(fedRampProduct.product.toLowerCase());
        } else if (searchFilter === "Provider") {
          return reg.test(fedRampProduct.provider.toLowerCase()) ||
                 reg.test(fedRampProduct?.missingProvider?.toLowerCase());
        } else {
          return reg.test(fedRampProduct.product.toLowerCase()) ||
                 reg.test(fedRampProduct?.missingProvider?.toLowerCase()) ||
                 reg.test(fedRampProduct.provider.toLowerCase()) ||
                 reg.test(fedRampProduct.serviceModel.toLowerCase()) ||
                 reg.test(fedRampProduct.impactLevel.toLowerCase()) ||
                 reg.test(fedRampProduct.authorizationStatus.toLowerCase());
        }
      });
    } else {
      return fedRampProducts;
    }
  }

  if (error) {
    return <div id="fedramp-wrapper" className="container-fluid">{GENERIC_ERROR_STRING}</div>;
  } else {
    return (<div id="fedramp-wrapper" className="container-fluid">
      <Header isLoggedIn={isLoggedIn} searchFilter={searchFilter} setSearchFilter={setSearchFilter}
              searchText={searchText} setSearchText={setSearchText} hoverModal={hoverModal}
              blurred={!(visibleColumn === null)}/>
      {modal}
      <SortableTable
        tableClassName="table_font table table-bordered table-condensed"
        data={getFilteredProducts()}
        dataMapper={(fedRampProduct) => <TableRow key={fedRampProduct.productSeq} fedRampProduct={fedRampProduct}
                                                  isLoggedIn={isLoggedIn} setModal={setModal}
                                                  visibleColumn={visibleColumn}/>}
        headerOptions={isLoggedIn ? [...headerOptions, loggedInHeaderOption] : headerOptions}
        initialSort={{ col: "Product", dir: "asc" }}
        isSelected={() => false}
        loading={loading}
        loadingMessage="Loading FedRAMP products"
        stickyHeader={true}
      />
      <ScrollToTopButton id="fedramp-back-to-top" color="primary"/>
    </div>);
  }
}
